/**
 * QUÉ HACER CON EL ANCLA DE VUELTA, EN CADA PASADA.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * POR QUÉ EXISTE ESTA FUNCIÓN (24-sep-2026, tras DOS arreglos fallidos)
 * ───────────────────────────────────────────────────────────────────────────────
 * La decisión vivía dentro de un `useEffect` de `Chats.js`, que no se puede cargar en un
 * test —necesita enrutador y Firebase—. Resultado: **el caso que fallaba no tenía test**,
 * y por eso el defecto llegó a producción dos veces seguidas.
 *
 * Es el mismo criterio que sacó `destinoDelPrivateReply` de `igWebhook` y la fusión de
 * chats del proveedor: lo que hay que poder probar no se deja dentro de lo que no se
 * puede montar.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * LOS DOS DEFECTOS QUE ESTO CIERRA, Y NINGUNO ERA EL QUE PARECÍA
 * ───────────────────────────────────────────────────────────────────────────────
 * **1 · La condición de reintento miraba la CANTIDAD, no el contenido.** La dependencia
 * del efecto era `listaVisible.length`. Con un filtro activo, cargar una página puede
 * añadir CERO coincidencias: la longitud no cambia, el efecto no vuelve a correr, y el
 * reintento no ocurre nunca. Ahora la pregunta es booleana y directa — **¿está el chat
 * del ancla en la lista visible?**— así que pasar de «no» a «sí» dispara siempre.
 *
 * **2 · El `requestAnimationFrame` se cancelaba a sí mismo.** El efecto programaba un
 * fotograma y su limpieza lo cancelaba; con un filtro activo la lista cambia muchas veces
 * seguidas —el agotado de páginas—, así que cada pasada mataba el fotograma de la
 * anterior y el scroll no llegaba a ejecutarse. Sin filtro hay uno o dos renders y el
 * fotograma sobrevivía: **por eso fallaba sólo con filtro**.
 *
 * El `rAF` era además innecesario: `useEffect` corre con el DOM ya confirmado, así que el
 * nodo existe cuando se busca. Se quitó.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ⚠️ UN CASO QUE FUE REAL HASTA EL 24-sep-2026, Y YA NO PUEDE OCURRIR
 * ───────────────────────────────────────────────────────────────────────────────
 * Aquí decía, y era cierto mientras se escribió:
 *
 *     «Con el filtro «no leídos», abrir un chat escribe `mensajesNoLeidos: 0` y el chat
 *      deja de cumplir el filtro. Ahí no está en la lista y no puede estarlo:
 *      `descartar` es la respuesta correcta, no un fallo. Es el ÚNICO filtro con esa
 *      propiedad — los demás no los toca abrir un chat.»
 *
 * **Dejó de poder ocurrir** cuando ese filtro cambió de fuente. Ya no lee un contador que
 * se apaga al abrir: lee `ultimoRemitente` (`utils/esperaDeRespuesta.js`), y **abrir un
 * chat no lo cambia** — sólo lo cambia que alguien responda. Así que el chat sigue
 * cumpliendo el filtro al volver y el ancla resuelve `ir`, no `descartar`.
 *
 * Dos cosas quedan de esto, y la segunda importa más que la primera:
 *
 *   1. **El ancla MEJORA**: volver de un chat con «Sin responder» puesto ahora te deja
 *      donde estabas. Antes se perdía, correctamente pero se perdía.
 *   2. **HOY NINGÚN FILTRO SE AUTO-INVALIDA AL ABRIR UN CHAT.** Era el único, y ya no lo
 *      hace. Si algún día alguien añade un filtro que dependa de un campo que la propia
 *      visita modifica, esta nota es el precedente: no es un defecto del ancla, y
 *      `descartar` seguirá siendo la respuesta correcta para ese caso.
 *
 * `decidirAncla` NO cambió: `descartar` sigue haciendo falta para un chat que está más
 * allá de lo cargado. Lo que desapareció es el único ejemplo de filtro que se
 * auto-invalidaba, no la rama que lo atendía.
 */

/**
 * @param {object} p
 * @param {string|null} p.ancla         el id guardado al salir, o null
 * @param {Array<string>} p.visibleIds  los ids que la lista está PINTANDO (ya filtrados)
 * @param {boolean} p.listaEstable      la lista ya no va a crecer
 * @param {boolean} [p.datosDeFiltroListos] ¿tiene el filtro activo su dato cargado?
 * @returns {'sin-ancla'|'ir'|'esperar'|'descartar'}
 *
 * `ir` → hacer scroll y consumir. `descartar` → consumir sin más. `esperar` → NO consumir:
 * puede aparecer en la siguiente carga.
 *
 * ⚠️ NUNCA devuelve «cargar más páginas». Esta función no pide datos: sólo reacciona a lo
 * que ya iba a llegar. Paginar para encontrar un chat costaría 96 documentos por un scroll
 * que nadie pidió.
 */
export function decidirAncla({ ancla, visibleIds, listaEstable, datosDeFiltroListos = true }) {
  if (!ancla) return 'sin-ancla';
  const ids = Array.isArray(visibleIds) ? visibleIds : [];
  if (ids.includes(ancla)) return 'ir';

  // ⚠️ NUNCA SE DESCARTA MIENTRAS EL FILTRO NO TIENE SU DATO. Ésta fue la causa real del
  // caso de «Solo con agenda», y el log la dio en una línea:
  //
  //     [ancla] DESCARTAR { cargados: 297, visibles: 0, enCargados: true }
  //
  // El chat estaba cargado y la lista visible vacía, porque las reservas llegan por un
  // `getDocs` aparte y todavía no estaban. Una lista sin resultados NO es una lista
  // terminada si el dato que la filtra aún no ha llegado — y tratarla como estable
  // gastaba el ancla en la primera pasada, sin reintento posible.
  if (!datosDeFiltroListos) return 'esperar';
  // No está. Si la lista todavía puede crecer, se espera — es el caso del chat que vive
  // en la página 2 y aún no ha llegado.
  return listaEstable ? 'descartar' : 'esperar';
}

/**
 * La firma de contenido que sirve como dependencia del efecto.
 *
 * ⚠️ NO es la longitud, y ésa era la mitad del defecto. Es **si el chat del ancla está
 * visible**: un booleano que cambia exactamente cuando hay algo que hacer, y que no se
 * mueve cuando la lista crece sin traerlo.
 */
export function anclaVisible(ancla, visibleIds) {
  if (!ancla) return false;
  return Array.isArray(visibleIds) && visibleIds.includes(ancla);
}
